import { useState, useCallback, useRef } from 'react';
import BulkActionService from '../services/BulkActionService';
import { FormattedUploadResults, TrackingResultItem } from '../types';

interface UploadState {
  file: File | null;
  results: TrackingResultItem[];
  uploading: boolean;
  refreshUpload: boolean;
}

const initialUploadState: UploadState = {
  file: null,
  results: [],
  uploading: false,
  refreshUpload: false
};

export interface UseOrderUploadResult {
  uploadState: UploadState;
  showUploadDialog: boolean;
  showUploadResults: boolean;
  uploadError: string | null;
  uploadTracking: () => Promise<TrackingResultItem[]>;
  setUploadFile: (file: File | null) => void;
  clearUploadResults: () => void;
  showUpload: () => void;
  hideUpload: () => void;
  toggleUploadResults: (show: boolean) => void;
  clearUploadError: () => void;
  getFormattedResults: () => FormattedUploadResults;
  setTrackingImportRef: (ref: HTMLInputElement | null) => void;
}

export function useOrderUpload(): UseOrderUploadResult {
  const [uploadState, setUploadState] = useState<UploadState>(initialUploadState);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const trackingImportRef = useRef<HTMLInputElement | null>(null);

  // Upload tracking file
  const uploadTracking = useCallback(async () => {
    try {
      // Clear previous results
      setUploadState(prev => ({ ...prev, results: [] }));

      const fileToUpload = uploadState.file;
      if (!fileToUpload) {
        throw new Error('Please select a file to upload');
      }

      // Validate file
      if (!BulkActionService.validateUploadFile(fileToUpload)) {
        throw new Error('Invalid file type. Please upload a CSV, XLSX, or TXT file.');
      }

      setUploadState(prev => ({ ...prev, uploading: true }));
      setUploadError(null);

      const results = await BulkActionService.uploadTracking(fileToUpload);

      setUploadState(prev => ({
        ...prev,
        results: results,
        refreshUpload: !prev.refreshUpload,
        uploading: false
      }));

      setShowUploadResults(true);

      // Clear file input
      if (trackingImportRef.current) {
        trackingImportRef.current.value = null;
      }

      return results;
    } catch (error: any) {
      console.error('Error uploading tracking:', error);
      setUploadError(error.message);
      setUploadState(prev => ({ ...prev, uploading: false }));
      throw error;
    }
  }, [uploadState.file]);

  // Set upload file
  const setUploadFile = useCallback((file: File | null) => {
    setUploadState(prev => ({ ...prev, file }));
  }, []);

  // Clear upload results
  const clearUploadResults = useCallback(() => {
    setUploadState(prev => ({
      ...prev,
      results: [],
      file: null,
      refreshUpload: !prev.refreshUpload
    }));
    setShowUploadResults(false);
  }, []);

  // Show upload dialog
  const showUpload = useCallback(() => {
    setUploadState(prev => ({ ...prev, file: null }));
    setShowUploadDialog(true);
    setShowUploadResults(false);
  }, []);

  // Hide upload dialog
  const hideUpload = useCallback(() => {
    setShowUploadDialog(false);
    setShowUploadResults(false);
  }, []);

  // Toggle upload results view
  const toggleUploadResults = useCallback((show: boolean) => {
    setShowUploadResults(show);
  }, []);

  // Clear upload error
  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  // Get formatted upload results
  const getFormattedResults = useCallback(() => {
    return BulkActionService.formatUploadResults(uploadState.results);
  }, [uploadState.results]);

  // Set tracking import ref
  const setTrackingImportRef = useCallback((ref: HTMLInputElement | null) => {
    trackingImportRef.current = ref;
  }, []);

  return {
    uploadState,
    showUploadDialog,
    showUploadResults,
    uploadError,
    uploadTracking,
    setUploadFile,
    clearUploadResults,
    showUpload,
    hideUpload,
    toggleUploadResults,
    clearUploadError,
    getFormattedResults,
    setTrackingImportRef
  };
}